import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { UserObj } from '@Reducers/User/Type';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootStateType } from '@Store';
import Modal from '@Components/Modal';
import UploadDocument from '@Components/DocumentUpload/UploadDocument';
import DocumentUploadSuccessfully from '@Components/DocumentUpload/DocumentUploadSuccessfully';
import DocumentDelete from '@Components/DocumentUpload/DocumentDelete';
import Title from '@Components/Title';
import SbButton from '@Components/Button';

import { getKycSetting } from '@Reducers/Global';

import SingleDocument from './SingleDocument';
import { Box, styled } from '@mui/system';
import { getAccountType } from '@Services/user';
import { KycType } from '@Reducers/kyc/Type';
import { docUploadSucModalClose, getUserKyc } from '@Reducers/kyc';
import { USER_STATUS_LIST } from '@Constants/user';
import SBDropdown from '@Components/Dropdown';
import { userLogin } from '@Services/hasAdminAccess';

interface PropsType {
  open: boolean;
  data?: UserObj;
  handleClose?: () => void;
  fetchList: () => void;
}

const SingleDocumentWrapper = styled(Grid)(() => ({
  border: '1px solid #b2b2b2',
  borderRadius: '8px',
}));

function KycModal({ open, handleClose, data, fetchList }: PropsType) {
  const isUserLogin = userLogin();
  const dispatch = useDispatch<AppDispatch>();
  const kycSetting = useSelector((state: RootStateType) => state.global.kycSetting);
  const isAccountHolder = !isUserLogin
    ? Boolean(data?._accountId?.isAccountHolder ?? (data as any)?.isAccountHolder ?? false)
    : useSelector((state: RootStateType) => state.loginUser.profile?._accountId.isAccountHolder) || false;
  const documentUploadSuccessfullyModal = useSelector(
    (state: RootStateType) => state.kyc.documentUploadSuccessfullyModal,
  );
  const _userId = data?._id || '';
  const _kyc = useSelector((state: RootStateType) => state.kyc.adminKyc?.[_userId] || {});
  const kycVerified = data?.onBoardedStatus === USER_STATUS_LIST.on_boarded.value;

  const [uploadDocumentModal, setUploadDocumentModal] = useState<boolean>(false);
  const [deleteDocumentModal, setDeleteDocumentModal] = useState<boolean>(false);
  const [successfullyModal, setSuccessfullyModal] = useState<boolean>(false);
  const [documentLabel, setDocumentLabel] = useState<string>('');
  const [documentEdit, setDocumentEdit] = useState<KycType | null>(null);
  const [deleteDocId, setDeleteDocId] = useState<string>('');
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [systemId, setSystemId] = useState<string>('');

  const individualDocumentLists = useMemo(() => {
    const accountObj = typeof data?._accountId === 'object' && data?._accountId ? data._accountId : data;
    if (kycSetting && accountObj) {
      const type = getAccountType(accountObj as any);
      switch (type) {
        case 'account-individual':
          return kycSetting.individualAccountHolder.list;
        case 'account-individual-company':
          return kycSetting.companyAccountHolder.list;
        case 'individual':
          return kycSetting.nonAccountHolder.list;
        default:
          return [];
      }
    }
    return [];
  }, [kycSetting, data]);

  const filteredSystemDropdownList = useMemo(() => {
    if (!loggedInUserSystemDropdownList?.length) return [];

    const systemValues: string[] = [];

    if (Array.isArray(data?._systems)) {
      data?._systems?.forEach((s: any) => {
        if (typeof s === 'string') systemValues.push(s);
        else if (s?._id) systemValues.push(s._id);
        else if (s?.name) systemValues.push(s.name);
        else if (s?.display) systemValues.push(s.display);
      });
    }

    if (Array.isArray((data as any)?.systems)) {
      ((data as any)?.systems as any[])?.forEach((s: any) => {
        if (typeof s === 'string') systemValues.push(s);
        else if (s?._id) systemValues.push(s._id);
        else if (s?.name) systemValues.push(s.name);
        else if (s?.display) systemValues.push(s.display);
      });
    }

    if (Array.isArray((data as any)?.needAttentionItems)) {
      ((data as any)?.needAttentionItems as any[])?.forEach((item: any) => {
        if (item?._systemId) systemValues.push(item._systemId);
        if (item?.systemName) systemValues.push(item.systemName);
      });
    }

    if (Array.isArray(data?._accountId?._company)) {
      data?._accountId?._company?.forEach((comp: any) => {
        if (comp?._systemId) systemValues.push(comp._systemId);
      });
    }

    if (!systemValues.length) {
      return loggedInUserSystemDropdownList;
    }

    const filtered = loggedInUserSystemDropdownList.filter(system =>
      systemValues.some(
        val =>
          val === system.display ||
          val === system.value ||
          (typeof val === 'string' && val.toLowerCase() === system.display?.toLowerCase()) ||
          (typeof val === 'string' && val.toLowerCase() === system.value?.toLowerCase()),
      ),
    );

    return filtered.length > 0 ? filtered : loggedInUserSystemDropdownList;
  }, [loggedInUserSystemDropdownList, data]);

  useEffect(() => {
    if (filteredSystemDropdownList?.length > 0) {
      setSystemId(prev => {
        if (prev && filteredSystemDropdownList.some(s => s.value === prev)) {
          return prev;
        }
        return filteredSystemDropdownList[0].value;
      });
    }
  }, [filteredSystemDropdownList]);

  useEffect(() => {
    if (!kycSetting) dispatch(getKycSetting());

    const hasAvailableSystems = filteredSystemDropdownList?.length > 0;
    const shouldWaitForSystemId = isAccountHolder || hasAvailableSystems;
    const isValidRequest = _userId && (shouldWaitForSystemId ? Boolean(systemId) : true);

    if (isValidRequest) {
      dispatch(
        getUserKyc({
          userId: _userId,
          ...(systemId ? { systemId } : {}),
        }),
      );
    }
  }, [_userId, systemId, isAccountHolder, filteredSystemDropdownList, kycSetting, dispatch]);

  useEffect(() => {
    if (documentUploadSuccessfullyModal) {
      setSuccessfullyModal(true);
    }
  }, [documentUploadSuccessfullyModal]);

  const uploadDocumentModalClose = () => setUploadDocumentModal(false);
  const uploadDocumentModalOpen = () => setUploadDocumentModal(true);

  const successModalClose = () => {
    dispatch(docUploadSucModalClose());
    uploadDocumentModalClose();
    setSuccessfullyModal(false);
    if (_userId) {
      dispatch(getUserKyc({ userId: _userId, systemId }));
      fetchList();
    }
  };

  const onDocumentUploadModalOpen = useCallback((documentLabel: string, documentData: KycType | null): void => {
    setDocumentLabel(documentLabel);
    setDocumentEdit(documentData);

    uploadDocumentModalOpen();
  }, []);

  const deleteDocumentModalClose = (refetch: boolean) => {
    setDeleteDocId('');
    setDeleteDocumentModal(false);
    if (refetch && _userId) {
      dispatch(getUserKyc({ userId: _userId, systemId }));
      fetchList();
    }
  };

  const onDocumentDeleteModalOpen = useCallback((id: string): void => {
    setDeleteDocId(id);
    setDeleteDocumentModal(true);
  }, []);

  const isCompanyAccount = data?._accountId?.accountType === 'company' || (data as any)?.accountType === 'company';

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          {/* Left Side Titles */}
          <Grid item>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Title removeBorder removePadding title="KYC Details" />
              <Title
                removeBorder
                removePadding
                title={`User Account No : ${data?._accountId?.accountNumber || (data as any)?.accountNumber || ''}`}
              />
            </Box>
          </Grid>

          {/* Right Side Dropdown */}

          {isAccountHolder && isCompanyAccount && (
            <Grid item>
              <Typography className="font-semibold my-1">Select a system to view KYC details</Typography>
              <SBDropdown
                required
                id="company-country-id"
                placeholder="Select System"
                value={[systemId || '']}
                options={filteredSystemDropdownList}
                onChange={([value]) => {
                  setSystemId(value);
                }}
              />
            </Grid>
          )}
        </Grid>
        <UploadDocument
          open={uploadDocumentModal}
          onClose={uploadDocumentModalClose}
          documentLabel={documentLabel}
          documentData={documentEdit}
          userId={data?._id || ''}
          systemId={systemId}
        />
        <DocumentUploadSuccessfully open={successfullyModal} onClose={successModalClose} />
        <DocumentDelete
          id={deleteDocId}
          userId={_userId}
          open={deleteDocumentModal}
          onClose={deleteDocumentModalClose}
          systemId={systemId}
        />
        <SingleDocumentWrapper container item>
          {individualDocumentLists.map((kyc, index) => (
            <SingleDocument
              key={`${kyc.label}-${index}`}
              label={kyc.label}
              required={kyc.isRequired}
              documentData={_kyc?.[kyc.label] || null}
              onDocumentUploadModalOpen={onDocumentUploadModalOpen}
              onDocumentDeleteModalOpen={onDocumentDeleteModalOpen}
              fetchList={fetchList}
              accountNumber={data?._accountId?.accountNumber || (data as any)?.accountNumber}
              kycVerified={kycVerified}
              systemId={systemId}
            />
          ))}
        </SingleDocumentWrapper>
        <SbButton
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '8px !important',
            mt: 5,
            mb: 1,
          }}
          className="w-full"
        >
          Close
        </SbButton>
      </Modal>
    </>
  );
}

export default KycModal;
